import type React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { Permission } from '@tekvoice/shared';
import { useAuth } from '@/auth/auth-context';
import { cn } from '@/lib/utils';

const tabs: { to: string; label: string; permission: Permission }[] = [
  { to: '/settings/voices', label: 'Voices & models', permission: 'settings.view' },
  { to: '/settings/generation', label: 'Generation', permission: 'settings.view' },
  { to: '/settings/publishing', label: 'Publishing', permission: 'settings.view' },
  { to: '/settings/connections', label: 'Connections', permission: 'settings.view' },
  { to: '/settings/users', label: 'Users', permission: 'user.manage' },
  { to: '/settings/audit', label: 'Audit', permission: 'audit.view' },
];

/** SPEC §9/§11 — tabs follow the shared permission matrix. */
export function SettingsLayout(): React.JSX.Element {
  const { can } = useAuth();
  const visibleTabs = tabs.filter((tab) => can(tab.permission));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Voices, generation limits, publishing format, users and the audit feed.
        </p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b pb-2">
        {visibleTabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
